import React, { useState, useEffect, useRef } from 'react';
import { Popover } from '@mui/material/';
import moment from 'moment';
import { Grid } from 'smart-webcomponents-react/grid';
import 'smart-webcomponents-react/source/styles/smart.default.css';
import NotesPopup from '../DynamicTable/NotesPopup';
import EventsPopup from '../DynamicTable/EventsPopup';
import {
    dateFormat, deepCopy, dateDiff, keyToLabel, convertArrayToObject, getDemandImpactColor, isDateInRange
} from "../../app/util";
import { dateColTemplate, dowColTemplate, formatCurrency, viewCurrencyTemplate, viewPercentageTemplate, getBarSellRate, formatNumberTemplate } from './groupIQHelper';
import './groupIQ.css';
import { useSelector } from "react-redux";

function WalkRate({ Smart, showAddMoreInfo, responseFromAPI }) {

    const { app } = useSelector((state) => state);
    const { app_state, ui_state } = app.current_state;
    const { currency_symbol } = ui_state;
    const { notes, avgImpactData, eventList } = app_state;

    const arrivalDate = responseFromAPI.data.primary.overall.arrival_date;
    const departureDate = responseFromAPI.data.primary.overall.departure_date;

    const gridRef = useRef(null);
    const dateValueRef = useRef(null);
    const barSellRate = useRef({});
    const [anchorEl, setAnchorEl] = React.useState(null);

    const columnNames = [];

    responseFromAPI.data.primary.dates[0].walk.forEach(item => {
        const col = item.room_type;
        columnNames.push(col);
    });

    const [gridHeight, setGridHeight] = useState({
        showAddMoreHeight: "",
        hideAddMoreHeight: "",
    });

    useEffect(() => {
        barSellRate.current = getBarSellRate(responseFromAPI.data.primary.dates, 'walk');
        setupWishRateData();
    }, [arrivalDate, departureDate])

    let roomblockDataArr = [];

    const ancillaryColumns = [];

    const keys = Object.keys(responseFromAPI.params_called_with.dates[0].ancillary);
    for (let key of keys) {
        if (!ancillaryColumns.includes(key)) {
            ancillaryColumns.push(key);
        }
    }
    ancillaryColumns.push("total")

    const calculateTotalGroupIQDataColumn = (data) => {

        let rateTotal = {};
        let totalGroupIQData = {
            totalRoomRev: { revenue: 0 },
            ancillaryRev: convertArrayToObject(ancillaryColumns, () => 0)
        }
        totalGroupIQData.ancillaryRev.total = 0

        columnNames.forEach(col => {
            if (!totalGroupIQData[col]) {
                totalGroupIQData[col] = { rooms: 0, rate: 0, discount: 0, roomRev: 0, displacedRev: 0, incrementalRev: 0 };
                rateTotal[col] = { rate: 0 };
            }
        })

        data.forEach(entry => {
            columnNames.forEach(col => {
                totalGroupIQData[col].rooms += entry[col].rooms;
                rateTotal[col].rate += entry[col].rate;
                totalGroupIQData[col].discount = (1 - (rateTotal[col].rate / barSellRate.current[col].bar_sell_rate_total)).toFixed(3);
                totalGroupIQData[col].roomRev += entry[col].roomRev;
                totalGroupIQData[col].displacedRev += entry[col].displacedRev;
                totalGroupIQData[col].incrementalRev += entry[col].incrementalRev;
                totalGroupIQData[col].rate = (totalGroupIQData[col].roomRev / totalGroupIQData[col].rooms).toFixed(0);
            })
            totalGroupIQData.totalRoomRev.revenue += entry.totalRoomRev.revenue;

            ancillaryColumns.forEach(col => {
                totalGroupIQData.ancillaryRev[col] += entry.ancillaryRev[col]
            });
        });
        return totalGroupIQData;
    };

    const totalGroupIQDataTemp = calculateTotalGroupIQDataColumn(roomblockDataArr);

    roomblockDataArr.push(totalGroupIQDataTemp);
    const groupIQNew = useRef(roomblockDataArr);

    const setupWishRateData = () => {
        let date = moment(arrivalDate).format("YYYY-MM-DD");
        let numDays = dateDiff(departureDate, arrivalDate);
        const roomblockDataArr = [];

        for (let index = 0; index < numDays; index++) {
            const currentDateData = responseFromAPI.data.primary.dates[index];
            const dataPoint = {
                stayDates: { date: date, dow: moment(date).format("ddd") },
                totalRoomRev: { revenue: currentDateData.walk_room_revenue },
                ancillaryRev: { ...responseFromAPI.params_called_with.dates[index].ancillary, total: currentDateData.ancillary_revenue }
            };

            currentDateData.walk.forEach(item => {
                const col = item.room_type;

                if (!dataPoint[col]) {
                    dataPoint[col] = { rooms: 0, rate: 0, discount: 0, roomRev: 0, displacedRev: 0, incrementalRev: 0 };
                }

                dataPoint[col].rooms = item.rooms;
                dataPoint[col].rate = item.rate;
                dataPoint[col].discount = item.discount;
                dataPoint[col].roomRev = item.revenue;
                dataPoint[col].displacedRev = item.displaced_revenue;
                dataPoint[col].incrementalRev = item.incremental_revenue;
            });

            roomblockDataArr.push(dataPoint);
            date = moment(date).add(1, 'days').format("YYYY-MM-DD");
        }

        const updatedGroupIQData = deepCopy(roomblockDataArr);
        const totalGroupIQData = calculateTotalGroupIQDataColumn(updatedGroupIQData);

        const lastRow = updatedGroupIQData[updatedGroupIQData.length - 1];
        if (!lastRow.stayDates) {
            updatedGroupIQData.pop();
        }
        updatedGroupIQData.push(totalGroupIQData);
        bindData(updatedGroupIQData);
    };

    const bindData = (roomblockDataArr) => {
        const newDataFields = [
            { name: 'date', map: 'stayDates.date', dataType: 'string' },
            { name: 'dow', map: 'stayDates.dow', dataType: 'string' },
            { name: 'totalRoomRev-revenue', map: 'totalRoomRev.revenue', dataType: 'string' },
        ];

        ancillaryColumns.forEach(col => {
            newDataFields.push(
                { name: `ancillaryRev-${col}`, map: `ancillaryRev.${col}`, dataType: 'string' }
            );
        });

        columnNames.forEach(col => {
            newDataFields.splice(newDataFields.length - 5, 0, { name: col + '-rooms', map: col + '.rooms', dataType: 'string' });
            newDataFields.splice(newDataFields.length - 5, 0, { name: col + '-rate', map: col + '.rate', dataType: 'string' })
            newDataFields.splice(newDataFields.length - 5, 0, { name: col + '-discount', map: col + '.discount', dataType: 'string' })
            newDataFields.splice(newDataFields.length - 5, 0, { name: col + '-roomRev', map: col + '.roomRev', dataType: 'string' })
            newDataFields.splice(newDataFields.length - 5, 0, { name: col + '-displacedRev', map: col + '.displacedRev', dataType: 'string' })
            newDataFields.splice(newDataFields.length - 5, 0, { name: col + '-incrementalRev', map: col + '.incrementalRev', dataType: 'string' })
        })

        if (gridRef.current) {
            gridRef.current.dataSource = new Smart.DataAdapter({
                dataSource: [...roomblockDataArr],
                dataFields: [...newDataFields],
            });
            groupIQNew.current = [...roomblockDataArr];
        }
    }

    const ancillaryColumnConfigs = ancillaryColumns.map(col => ({
        label: keyToLabel(col), // Capitalize the first letter
        dataField: `ancillaryRev-${col}`,
        columnGroup: 'ancillaryRev',
        labelTemplate: `<div id="secondary-header">${keyToLabel(col)}</div>`,
        minWidth: 64,
        freeze: 'far',
        allowEdit: false,
        template: viewCurrencyTemplate(currency_symbol, formatCurrency),
    }));

    const initialColumns = [
        {
            label: 'Date',
            dataField: 'date',
            columnGroup: 'staydates',
            allowEdit: false,
            allowResize: false,
            freeze: 'near',
            width: 90,
            labelTemplate: '<div id="date-dow">Date</div>',
            template: dateColTemplate(dateFormat, notes),
        },
        {
            label: 'DOW',
            dataField: 'dow',
            columnGroup: 'staydates',
            allowEdit: false,
            allowResize: false,
            freeze: 'near',
            width: 90,
            labelTemplate: '<div id="date-dow">DOW</div>',
            template: dowColTemplate(dateFormat, avgImpactData, getDemandImpactColor, eventList, isDateInRange),
        },

        {
            label: 'Revenue',
            dataField: 'totalRoomRev-revenue',
            columnGroup: 'totalRoomRev',
            labelTemplate: '<div id="secondary-header">Revenue</div>',
            minWidth: 130,
            freeze: 'far',
            allowEdit: false,
            template: viewCurrencyTemplate(currency_symbol, formatCurrency, 'cell-right-border'),
        },
        ...ancillaryColumnConfigs
    ]

    const initialColumnGroups = [
        {
            label: dateFormat(arrivalDate, "YYYY"),
            labelTemplate: '<div id="primary-header">' + dateFormat(arrivalDate, "YYYY") + '</div>',
            name: 'staydates',
        },

        {
            label: 'Total Room Rev',
            labelTemplate: '<div id="primary-header">Total Room Rev</div>',
            name: 'totalRoomRev',
        },
        {
            label: 'Ancillary Revenue',
            labelTemplate: '<div id="primary-header">Ancillary Revenue</div>',
            name: 'ancillaryRev',
        },
    ];

    columnNames.forEach((col, index) => {
        initialColumnGroups.splice(initialColumnGroups - 2, 0, {
            label: keyToLabel(col),
            labelTemplate: '<div id="primary-header">' + keyToLabel(col) + '</div>',
            name: col,
        })
        initialColumns.splice(initialColumns.length - 6, 0, {
            label: 'Rooms',
            labelTemplate: '<div id="secondary-header">Rooms</div>',
            dataField: col + '-rooms',
            columnGroup: col,
            minWidth: 70,
            allowEdit: false,
            template: formatNumberTemplate(),
        })

        initialColumns.splice(initialColumns.length - 6, 0, {
            label: 'Rate',
            dataField: col + '-rate',
            columnGroup: col,
            labelTemplate: '<div id="secondary-header">Rate</div>',
            minWidth: 70,
            template: viewCurrencyTemplate(currency_symbol, formatCurrency),
            allowEdit: false,
        })
        initialColumns.splice(initialColumns.length - 6, 0, {
            label: 'Discount',
            labelTemplate: '<div id="secondary-header">Discount</div>',
            dataField: col + '-discount',
            columnGroup: col,
            minWidth: 70,
            allowEdit: false,
            template: viewPercentageTemplate(),
        })
        initialColumns.splice(initialColumns.length - 6, 0, {
            label: 'Room Rev',
            dataField: col + '-roomRev',
            columnGroup: col,
            labelTemplate: '<div id="secondary-header">Room Rev</div>',
            minWidth: 70,
            allowEdit: false,
            template: viewCurrencyTemplate(currency_symbol, formatCurrency),
        })
        initialColumns.splice(initialColumns.length - 6, 0, {
            label: 'Displaced Rev',
            labelTemplate: '<div id="secondary-header">Displaced Rev</div>',
            dataField: col + '-displacedRev',
            columnGroup: col,
            minWidth: 70,
            allowEdit: false,
            template: viewCurrencyTemplate(currency_symbol, formatCurrency),
        })
        initialColumns.splice(initialColumns.length - 6, 0, {
            label: 'Incremental Rev',
            dataField: col + '-incrementalRev',
            columnGroup: col,
            labelTemplate: '<div id="secondary-header">Incremental Rev</div>',
            minWidth: 80,
            allowEdit: false,
            template: viewCurrencyTemplate(currency_symbol, formatCurrency, "cell-right-border"),
        })

    })

    const editing = {
        enabled: true,
        mode: 'cell'
    };
    const behavior = {
        columnResizeMode: 'growAndShrink'
    };
    const appearance = {
        alternationCount: 2,
        showColumnHeaderLines: false,
        showColumnLines: false,
        showRowLines: false,
    };
    const onRowInit = (index, row) => {
        if (index === gridRef.current.dataSource.length - 1) {
            row.freeze = 'far';
        }
    }
    //Smart Grid params functions Ends
    const [columns, setColumns] = useState(deepCopy(initialColumns));
    const [columnGroups, setColumnGroups] = useState(initialColumnGroups);

    const setTableHeight = () => {
        let windowHeight = window.innerHeight;
        let config = { showAddMoreHeight: "", hideAddMoreHeight: "" };
        config = { showAddMoreHeight: windowHeight - 477, hideAddMoreHeight: windowHeight - 597 };
        setGridHeight(config);
    };

    useEffect(() => {
        setTableHeight();
        window.addEventListener("resize", setTableHeight);

        return () => {
            window.removeEventListener("resize", setTableHeight);
        };
    }, [showAddMoreInfo]);

    let clickElementEvent;
    const onBeginEdit = (event) => {

        if (clickElementEvent.detail.originalEvent.target.classList.contains('note-icon-span')) {
            handleClick(clickElementEvent.detail.originalEvent, event.detail.data.date);
        }

        if (clickElementEvent.detail.originalEvent.target.classList.contains('event-icon-span')) {
            handleClick(clickElementEvent.detail.originalEvent, event.detail.data.date);
        }

    }
    const onCellClick = (event) => {
        clickElementEvent = event;
    }

    const handleClick = (event, dateValue) => {

        dateValueRef.current = dateValue;
        setAnchorEl(event);

        if (event.target.className === 'note-icon-span') {
            let noteIconDiv = document.getElementsByClassName('note-icon-div');
            noteIconDiv[0].click();
        }

        if (event.target.className === 'event-icon-span') {
            let noteIconDiv = document.getElementsByClassName('event-icon-div');
            noteIconDiv[0].click();
        }

    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'outerPopupCover' : undefined;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: showAddMoreInfo ? `${gridHeight.hideAddMoreHeight}px` : `${gridHeight.showAddMoreHeight}px`,
            overflow: 'hidden'
        }}>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                anchorReference="anchorPosition"
                anchorPosition={anchorEl && { top: anchorEl.clientY, left: anchorEl.clientX }}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                className='outer-popup-cover'
                style={{ opacity: 0, visibility: 'hidden' }}
            >

                {anchorEl && anchorEl.target.className === 'note-icon-span' && <NotesPopup params={{ stay_date: dateValueRef.current, readonly: true }} outerHandleClose={handleClose} />}
                {anchorEl && anchorEl.target.className === 'event-icon-span' && <EventsPopup params={{ stay_date: dateValueRef.current }} outerHandleClose={handleClose} />}
            </Popover>

            <Grid
                style={{ flexGrow: 1 }}
                columns={columns}
                ref={gridRef}
                columnGroups={columnGroups}
                editing={editing}
                behavior={behavior}
                appearance={appearance}
                onRowInit={onRowInit}
                onBeginEdit={onBeginEdit}
                className='walk-rate'
                onCellClick={onCellClick}
            ></Grid>

        </div>
    )
}

export default WalkRate
